/**
 * Utility to detect and render convergence periods as overlays on the chart
 */

import { PivotChartDataPoint } from '../../../types/pivotPoints.ts';
import { calculateConvergence } from './convergenceCalculator';

export interface ConvergencePeriod {
  startTime: number;
  endTime: number;
  level: 'convergence' | 'tight';
  avgRatio: number;
}

/**
 * Detect periods of convergence from pivot data
 * @param pivotData Array of pivot data points
 * @param extendToCurrentTime If true, extends the last convergence period to current time (since the last pivot is still valid)
 */
export function detectConvergencePeriods(
  pivotData: PivotChartDataPoint[],
  extendToCurrentTime: boolean = true
): ConvergencePeriod[] {
  if (!pivotData || pivotData.length === 0) return [];

  const periods: ConvergencePeriod[] = [];
  let currentPeriod: ConvergencePeriod | null = null;
  let ratioSum = 0;
  let ratioCount = 0;

  pivotData.forEach((point, index) => {
    const convergence = calculateConvergence(point.r1, point.s1, point.pp);

    if (convergence.level !== 'none') {
      // Start or continue a convergence period
      if (!currentPeriod) {
        currentPeriod = {
          startTime: point.timestamp,
          endTime: point.timestamp,
          level: convergence.level,
          avgRatio: convergence.ratio,
        };
        ratioSum = convergence.ratio;
        ratioCount = 1;
      } else {
        // Extend current period
        currentPeriod.endTime = point.timestamp;
        ratioSum += convergence.ratio;
        ratioCount++;
        currentPeriod.avgRatio = ratioSum / ratioCount;

        // Upgrade level if we hit a tighter convergence
        if (convergence.level === 'tight' && currentPeriod.level === 'convergence') {
          currentPeriod.level = 'tight';
        }
      }
    } else {
      // End current period if exists
      if (currentPeriod) {
        periods.push(currentPeriod);
        currentPeriod = null;
        ratioSum = 0;
        ratioCount = 0;
      }
    }

    // If this is the last point and we have an active period, close it
    // If extendToCurrentTime is true and this is the last pivot point with convergence,
    // extend the period to current time since the pivot is still valid
    if (index === pivotData.length - 1 && currentPeriod) {
      if (extendToCurrentTime) {
        // Extend to current time since the last pivot point is still valid
        const now = new Date();
        now.setSeconds(0, 0); // Truncate seconds and milliseconds for consistency
        currentPeriod.endTime = now.getTime();
      }
      periods.push(currentPeriod);
    }
  });

  return periods;
}

/**
 * Get color for convergence overlay based on level
 */
export function getConvergenceOverlayColor(level: 'convergence' | 'tight'): string {
  switch (level) {
    case 'tight':
      return 'rgba(255, 140, 0, 0.15)'; // Orange with transparency
    case 'convergence':
      return 'rgba(255, 215, 0, 0.1)'; // Gold with transparency
    default:
      return 'rgba(255, 215, 0, 0.1)';
  }
}

